import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { OAuthUserInfo } from '../../models/oauthuserinfo.model';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { AuthorizationConfig } from '../../models/authorization_config.model';
import CustomQueryStringParser from './custom_query_string_parser';
import { PermissionService } from '../permission.service';

import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  GRANT_TYPE_AUTHORIZATION_CODE,
  RedirectRequestHandler,
  Requestor,
  TokenRequest,
  TokenRequestHandler,
  TokenResponse,
  DefaultCrypto,
  GRANT_TYPE_REFRESH_TOKEN,
} from '@openid/appauth';
import { SessionStorageService } from '../session-storage.service';
import { AppConfig } from 'src/app/app-config';
import { TermsService } from '../terms.service';

const SS_ISSUER_URI = 'authorization.service.issuer_uri' + '?idp=http://adfs.arvdev.addev.arvest.com/adfs/services/trust';
const SS_USER_INFO = 'authorization.service.user_info';
const SS_OPENID_CONFIG = 'authorization.service.parsed_openid_configuration';
const SS_TOKEN_RESPONSE = 'authorization.service.token_response';
const REFRESH_TOKEN_KEY = 'refresh_token';
const CODE_VERIFIER_KEY = 'code_verifier';

@Injectable()
export class OAuthService {

  private notifier = new AuthorizationNotifier();
  private authorizationHandler = new RedirectRequestHandler(undefined, new CustomQueryStringParser());
  private tokenHandler: TokenRequestHandler;

  private tokenResponses: BehaviorSubject<TokenResponse>;
  private serviceConfigs: BehaviorSubject<AuthorizationServiceConfiguration>;

  private userInfos: BehaviorSubject<OAuthUserInfo>;
  private userInfo$: Observable<OAuthUserInfo>;

  constructor(private router: Router, @Inject('AuthorizationConfig')
              private environment: AuthorizationConfig,
              private http: HttpClient,
              private requestor: Requestor,
              private sessionStorage: SessionStorageService,
              private permissionsService: PermissionService,
              private appConfig: AppConfig,
              private termsService: TermsService) {

    this.tokenHandler = new BaseTokenRequestHandler(requestor);
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    this.notifier.setAuthorizationListener((request, response, error) => {
      // console.log('Authorization request complete ', request, response, error);
      const state = request.state || null;
      if (response) { this.completeAuthorizationRequest(response.code, request.internal.code_verifier, state); }
    });


    // attempt to restore previous values of the metadata config, token response
    let authorizationServiceConfiguration: AuthorizationServiceConfiguration | null = null;
    let tokenResponse: TokenResponse | null = null;
    let userInfo: OAuthUserInfo | null = null;

    // verify that we are still working with the same IDP, since a reload may
    // have been due to an underlying configuration change
    if (environment.issuer_uri === window.sessionStorage.getItem(SS_ISSUER_URI)) {
      const serviceConfigJSON = JSON.parse(
        window.localStorage.getItem(SS_OPENID_CONFIG));
      authorizationServiceConfiguration = serviceConfigJSON &&
        new AuthorizationServiceConfiguration(serviceConfigJSON);

      try {
        const tokenResponseJSON = JSON.parse(window.localStorage.getItem(SS_TOKEN_RESPONSE));
        tokenResponse = tokenResponseJSON && new TokenResponse(tokenResponseJSON);
        if (!tokenResponse.accessToken) {
          throw new Error();
        }
      } catch {
        tokenResponse = null;
      }

      userInfo = JSON.parse(window.localStorage.getItem(SS_USER_INFO));
    } else {
      // new issuer (or first run, or cleared session)
      // make sure we store the issuer, and have no other state
      window.sessionStorage.setItem(SS_ISSUER_URI, environment.issuer_uri);
      window.localStorage.removeItem(SS_OPENID_CONFIG);
      window.localStorage.removeItem(SS_TOKEN_RESPONSE);
    }

    // create subjects with the current values (or null)
    this.tokenResponses = new BehaviorSubject(tokenResponse);
    this.serviceConfigs = new BehaviorSubject(authorizationServiceConfiguration);
    this.userInfos = new BehaviorSubject(userInfo);
    this.userInfo$ = this.userInfos.asObservable();
    // update local storage on changes
    this.serviceConfigs.subscribe((config: AuthorizationServiceConfiguration) => {
      console.log("testing config", config);
      window.sessionStorage.setItem(SS_OPENID_CONFIG, config && JSON.stringify(config.toJson()));
    });

    this.tokenResponses.subscribe((token: TokenResponse) => {
      console.log("tokenResponse", token);
      this.sessionStorage.setTokenResponse(token);
      // window.sessionStorage.setItem(SS_TOKEN_RESPONSE, token && JSON.stringify(token.toJson()));
    });

    this.userInfos.subscribe((info: OAuthUserInfo) => {
      console.log("userInfo", info);
      this.sessionStorage.setUserInfos(info);
      // window.sessionStorage.setItem(SS_USER_INFO, info && JSON.stringify(info));
    });

    // monitor changes in metadata/tokens to possibly clear dependent values,
    // and to fetch userInfo.
    combineLatest(this.serviceConfigs, this.tokenResponses).subscribe(
      ([configuration, token]: [AuthorizationServiceConfiguration, TokenResponse]) => {

        // if the service config is cleared, we need to invalidate any TokenResponse/userInfo
        if (configuration == null) {
          if (token != null) {
            this.tokenResponses.next(null);
          }
          this.userInfos.next(null);
          return;
        }

        // if the token is cleared, assume userinfo is invalidated too
        if (token == null) {
          this.userInfos.next(null);
          return;
        }

        // if we don't have a user info endpoint, we can't fetch user info
        if (configuration.userInfoEndpoint == null) {
          this.userInfos.next(null);
          return;
        }

        // fetch user info, if none
        if (this.userInfos.value == null) {
          const accessToken = token.accessToken;
          this.http.get(configuration.userInfoEndpoint, {
            headers: {
              'Skip-Interceptor': '',
              Authorization: `Bearer ${accessToken}`
            },
          }).subscribe( (userInfoResponse: OAuthUserInfo) => {
            this.userInfos.next(userInfoResponse);
          });
        }

      });

    // start fetching metadata
    if (authorizationServiceConfiguration == null) {
      this.fetchServiceConfiguration(environment);
    }
  }

  get issuerUri(): string {
    return this.environment.issuer_uri;
  }

  get expectedMetadataUri(): string {
    return `${this.issuerUri}/.well-known/openid-configuration`;
  }

  public serviceConfiguration(): Observable<AuthorizationServiceConfiguration> {
    return this.serviceConfigs.asObservable().pipe(distinctUntilChanged());
  }

  public tokenResponse(): Observable<TokenResponse> {
    return this.tokenResponses.asObservable().pipe(distinctUntilChanged());
  }

  authorize(state: string): void {
    this.serviceConfigs
      .pipe(filter((value: any) => value != null))
      .pipe(take(1))
      .subscribe((configuration: AuthorizationServiceConfiguration) => {
        const scope = this.environment.scope || 'openid';

        // console.log("About to kick off the authorize process", {state});

        // create a request
        const request = new AuthorizationRequest({
          client_id: this.environment.client_id,
          redirect_uri: this.environment.redirect_uri,
          scope,
          response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
          state,
          extras: this.environment.extras
        }, new DefaultCrypto(), true);

        this.authorizationHandler.performAuthorizationRequest(configuration, request);
      });
  }

  checkForAuthorizationResponse() {
    this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  completeAuthorizationRequest(code: string, codeVerifier: string, state: string): any {
    return new Promise((resolve, reject) => {
      this.serviceConfigs
        .pipe(filter((value: any) => value != null))
        .pipe(take(1))
        .subscribe((configuration: AuthorizationServiceConfiguration) => {
          // use the code to make the token request.
          const extras = {};
          extras[CODE_VERIFIER_KEY] = codeVerifier;
          let tokenRequest;
          const token = this.sessionStorage.getTokenResponse();
          if ( token ) {
            tokenRequest = new TokenRequest({
              client_id: this.environment.client_id,
              redirect_uri: this.environment.redirect_uri,
              grant_type: GRANT_TYPE_REFRESH_TOKEN,
              code,
              refresh_token: this.sessionStorage.getTokenResponse()[REFRESH_TOKEN_KEY],
              extras
            });
          } else {
            tokenRequest = new TokenRequest({
              client_id: this.environment.client_id,
              redirect_uri: this.environment.redirect_uri,
              grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
              code,
              refresh_token: undefined,
              extras
            });
          }

          this.tokenHandler.performTokenRequest(configuration, tokenRequest).then( (response: TokenResponse) => {
            // console.log("completeAuthorizationRequest", response);

            this.tokenResponses.next(response);
            if (!token) {
              this.router.navigate(['']);
            }

            this.userInfo$.pipe(distinctUntilChanged()).subscribe( (userInfo: OAuthUserInfo) => {
              if (!userInfo) { return; }
              // console.log("Got userinfo, going to check for permissions and terms", userInfo);

              // check their permissions
              this.permissionsService.checkPermissions();

              // now chech for T&C agreements
              this.termsService.verify(userInfo.sub).then(() => {
                // they are good to go!
                this.router.navigate([state]);
              }).catch((error: any) => {
                // oops, need to sign something new
                this.router.navigate(['/terms-and-conditions']);
              });

            });

          }).catch((error: any) => {
            console.log('Failed token request call: ', error);
          });
        });

    });
  }


  authorizeWithRefreshToken(state: string): void {

    this.serviceConfigs
      .pipe(filter((value: any) => value != null))
      .pipe(take(1))
      .subscribe((configuration: AuthorizationServiceConfiguration) => {
        let request: TokenRequest;
        const token = this.sessionStorage.getTokenResponse();
        console.log('Attempting to re auth using local storage data');
        let refreshToken = '';
        if (this.sessionStorage.getTokenResponse()) {
          refreshToken = this.sessionStorage.getTokenResponse()[REFRESH_TOKEN_KEY];
        }
        console.log('The refresh Token is now set to :' + refreshToken);
        request = new TokenRequest({
          client_id: this.environment.client_id,
          redirect_uri: this.environment.redirect_uri,
          grant_type: GRANT_TYPE_REFRESH_TOKEN,
          code: undefined,
          refresh_token: refreshToken,
          extras: undefined
        });

        this.tokenHandler.performTokenRequest(configuration, request).then((response: TokenResponse) => {
            console.log('Returned from reauth with the following data' + response);
            this.tokenResponses.next(response);
            this.userInfo$.pipe(distinctUntilChanged()).subscribe((userInfo: OAuthUserInfo) => {
              if (!token) {
                this.router.navigate(['']);
              }

              if (!userInfo) {
                return;
              }
              // console.log("Got userinfo, going to check for permissions and terms", userInfo);

              // check their permissions
              this.permissionsService.checkPermissions();

              // now chech for T&C agreements
              this.termsService.verify(userInfo.sub).then(() => {
                // they are good to go!
                this.router.navigate([state]);
              }).catch((error: any) => {
                // oops, need to sign something new
                this.router.navigate(['/terms-and-conditions']);
              });

            });
          },
          () => this.authorize(state));
      });
  }

  signOut(): void {
    this.serviceConfigs
      .pipe(filter((value: any) => value != null))
      .pipe(take(1))
      .subscribe((configuration: AuthorizationServiceConfiguration) => {
        this.tokenResponses.subscribe((token: TokenResponse) => {
          if (token && token.accessToken) {

            const queryString = 'client_id=' +
              this.environment.client_id +
              '&token=' + token.accessToken +
              '&token_type_hint=access_token';
            const headers = { headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Skip-Interceptor': ''
            }};

            // this revokes the access token/refersh token for this session
            this.sessionStorage.clearAll();
            this.http.post(configuration.revocationEndpoint, queryString, headers).subscribe( (result: any) => {
              // console.log('Revocation endpoint hit', result);

              this.sessionStorage.clearAll();

              this.router.navigate(['/externalRedirect', { externalUrl: this.environment.ping_end_session_endpoint +
                  '?TargetResource=' + this.appConfig.config.logoutRedirectUrl }]);

            }, (error: any) => {
              console.log('Error hitting revocation endpoint', error);
            });
          }
        });
      });
  }

  emptySessionStorage() {
    window.sessionStorage.removeItem(SS_ISSUER_URI);
    window.sessionStorage.removeItem(SS_OPENID_CONFIG);
    window.localStorage.removeItem(SS_TOKEN_RESPONSE);
  }

  private async fetchServiceConfiguration(configuration: AuthorizationConfig) {
    const response = await AuthorizationServiceConfiguration.fetchFromIssuer(configuration.issuer_uri, this.requestor);
    this.serviceConfigs.next(response);
  }

}
